import { intValue, mapValue, stringValue } from "@/utils/coerce";

export type ToolCallSummary = {
  globalIndex: number;
  sequence: number | null;
  name: string;
  status: string;
  durationMs: number;
  raw: Record<string, unknown>;
};

export type ToolCallGroup = {
  index: number;
  turnId: string;
  calls: ToolCallSummary[];
};

// Prefer the recorded duration_ms; fall back to completed_at - created_at when
// it's null (the replay snapshot leaves duration_ms unset but keeps timestamps).
export function durationMsOf(call: Record<string, unknown>): number {
  const recorded = intValue(call.duration_ms);
  if (recorded != null) return recorded;
  const start = stringValue(call.created_at);
  const end = stringValue(call.completed_at);
  if (start && end) {
    const ms = Date.parse(end) - Date.parse(start);
    if (Number.isFinite(ms) && ms >= 0) return ms;
  }
  return 0;
}

export function toolCallGroups(toolCalls: unknown[]): ToolCallGroup[] {
  const groups: ToolCallGroup[] = [];
  const byTurn = new Map<string, ToolCallGroup>();
  let fallbackTurnIndex = 0;
  let fallbackGroup: ToolCallGroup | null = null;
  let lastFallbackSequence: number | null = null;

  toolCalls.forEach((raw, index) => {
    const call = mapValue(raw);
    if (Object.keys(call).length === 0) return;
    const turnId = stringValue(call.turn_id, "");
    const sequence = intValue(call.sequence);
    let group: ToolCallGroup;
    if (!turnId) {
      const startsNewFallbackTurn =
        !fallbackGroup ||
        sequence === null ||
        (lastFallbackSequence !== null && sequence <= lastFallbackSequence);
      if (startsNewFallbackTurn) {
        fallbackTurnIndex += 1;
        fallbackGroup = {
          index: groups.length + 1,
          turnId: `fallback-${fallbackTurnIndex}`,
          calls: [],
        };
        groups.push(fallbackGroup);
      }
      group = fallbackGroup!;
      lastFallbackSequence = sequence;
    } else {
      const existing = byTurn.get(turnId);
      if (existing) {
        group = existing;
      } else {
        group = {
          index: groups.length + 1,
          turnId,
          calls: [],
        };
        byTurn.set(turnId, group);
        groups.push(group);
      }
    }
    group.calls.push({
      globalIndex: index + 1,
      sequence,
      name: stringValue(call.tool_name, stringValue(call.function, "tool")),
      status: stringValue(call.status, "-"),
      durationMs: durationMsOf(call),
      raw: call,
    });
  });

  return groups.filter((group) => group.calls.length > 0);
}

export function maxDuration(group: ToolCallGroup) {
  return group.calls.reduce(
    (max, call) => Math.max(max, call.durationMs),
    0,
  );
}
